import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Select, type SelectOption } from '@/components/ui/Select';
import { container } from '@/services/container';
import { useReportStore } from '@/store/reports';
import { useUiStore } from '@/store/ui';
import { MeshLinkError } from '@/lib/result';
import type { Address } from '@shared/chain';
import type { Report } from '@shared/types';

const TARGET_OPTIONS: SelectOption[] = [
  { value: 'post', label: '帖子' },
  { value: 'user', label: '用户' },
  { value: 'message', label: '消息' },
];

const REASON_OPTIONS: SelectOption[] = [
  { value: 'spam', label: '垃圾广告' },
  { value: 'harassment', label: '骚扰辱骂' },
  { value: 'illegal', label: '违法违规' },
  { value: 'other', label: '其他' },
];

export interface ReportDialogProps {
  open: boolean;
  onClose: () => void;
  defaultTargetType?: Report['targetType'];
  defaultTargetId?: string;
}

/** 举报弹窗：本地落库 + 链上提交（治理闭环：举报上链，达阈值自动下架）。 */
export function ReportDialog({
  open,
  onClose,
  defaultTargetType = 'post',
  defaultTargetId = '',
}: ReportDialogProps): JSX.Element {
  const [targetType, setTargetType] = useState<Report['targetType']>(defaultTargetType);
  const [targetId, setTargetId] = useState(defaultTargetId);
  const [reason, setReason] = useState('spam');
  const [detail, setDetail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async (): Promise<void> => {
    if (!targetId.trim() || !reason) {
      useUiStore.getState().pushError('E_INVALID_INPUT');
      return;
    }
    setSubmitting(true);
    try {
      const report: Report = {
        id: `rpt_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`,
        targetType,
        targetId: targetId.trim(),
        reason,
        detail: detail.trim() || undefined,
        ts: Date.now(),
        status: 'local',
      };
      await container.storage.reports.put(report);
      useReportStore.getState().add(report);

      // 治理闭环：帖子举报（anchor 目标）提交链上，达阈值自动下架；user/message 保持本地。
      // 链端 report op 仅接受 anchor/did/social；前端 post 对应 anchor（targetId 为内容 cid）。
      if (targetType === 'post') {
        let chainStatus: 'chain' | 'chain_failed' = 'chain';
        let autoTombstone = false;
        try {
          const res = await container.chain.reportContent({
            from: container.identity.address as Address,
            targetType: 'anchor',
            targetId: report.targetId,
            reason: report.reason,
            signature: report.detail,
          });
          autoTombstone = Boolean(res.autoTombstone);
          if (res.dup) chainStatus = 'chain';
        } catch (err) {
          // 上链失败不阻塞本地记录：toast 提示，状态标记为待补交
          chainStatus = 'chain_failed';
          useUiStore.getState().pushError(err instanceof MeshLinkError ? err.code : 'E_UNKNOWN');
        }
        report.status = autoTombstone ? 'auto_tombstoned' : chainStatus;
        await container.storage.reports.put(report);
        useReportStore.getState().add(report);
        useUiStore.getState().pushToast(
          'success',
          autoTombstone
            ? '举报已确认，内容已自动下架'
            : chainStatus === 'chain'
              ? '举报已提交链上，确认后将自动下架'
              : '举报已记录（链上提交失败，将重试）',
        );
      } else {
        useUiStore.getState().pushToast('success', '举报已记录（本地）');
      }
      setDetail('');
      onClose();
    } catch (err) {
      useUiStore.getState().pushError(err instanceof MeshLinkError ? err.code : 'E_UNKNOWN');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title="举报"
      description="举报信息仅保存在本机，等待链上提交。"
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose}>
            取消
          </Button>
          <Button size="sm" loading={submitting} onClick={() => void submit()}>
            提交
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <label className="block text-xs text-dim">
          举报类型
          <div className="mt-1">
            <Select
              value={targetType}
              onChange={(v) => setTargetType(v as Report['targetType'])}
              options={TARGET_OPTIONS}
            />
          </div>
        </label>
        <label className="block text-xs text-dim">
          对象 ID
          <div className="mt-1">
            <Input
              value={targetId}
              onChange={(e) => setTargetId(e.target.value)}
              placeholder="帖子 ID / DID / 消息 ID"
            />
          </div>
        </label>
        <label className="block text-xs text-dim">
          原因
          <div className="mt-1">
            <Select value={reason} onChange={setReason} options={REASON_OPTIONS} />
          </div>
        </label>
        <label className="block text-xs text-dim">
          补充说明（可选）
          <div className="mt-1">
            <Input value={detail} onChange={(e) => setDetail(e.target.value)} placeholder="可补充说明" />
          </div>
        </label>
      </div>
    </Modal>
  );
}

export default ReportDialog;
